import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Avatar,
  Button,
  Divider,
  Skeleton,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextInputComponent from "../../components/ui/TextInputComponent";
import { useToast } from "../../components/ui/ToastContext";
import { useActAsClient } from "./ActAsClientContext";

// ✅ BOTH auth instances
import { auth } from "../../firebase-config";
import { authPortal } from "../../firebase-portal";

const GET_CLIENT_PROFILE_URL =
  process.env.REACT_APP_GET_CLIENT_PROFILE_URL ||
  "https://getclientprofile-774073159618.us-central1.run.app";

const UPDATE_CLIENT_PROFILE_URL =
  process.env.REACT_APP_UPDATE_CLIENT_PROFILE_URL ||
  "https://updateclientprofile-774073159618.us-central1.run.app";

const UPLOAD_CLIENT_PHOTO_URL =
  process.env.REACT_APP_UPLOAD_CLIENT_PHOTO_URL ||
  "https://uploadclientphoto-774073159618.us-central1.run.app";

const fmtYmd = (d) => (d && dayjs(d).isValid() ? dayjs(d).format("YYYY-MM-DD") : "");

// Upload constraints
const MAX_MB = 50;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

export default function ClientPortalInfo() {
  const [profile, setProfile] = useState({
    fullName: "",
    email: "",
    phone: "",
    altEmail: "",
    photoURL: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
    dateOfBirth: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const actAs = useActAsClient();
  const [params] = useSearchParams();
  const isReadOnly = actAs.isActingAs;

  // ---- Toast adapter: ALWAYS call with (message, severity) ----
  const toastCtx = useToast?.();
  const toast = useCallback(
    (message, severity = "info") => {
      const tryCall = (fn) => {
        try {
          fn(message, severity);
          return true;
        } catch {
          return false;
        }
      };
      if (!toastCtx) {
        return severity === "error" ? console.error(message) : console.log(message);
      }
      if (typeof toastCtx.toast === "function" && tryCall(toastCtx.toast)) return;
      if (typeof toastCtx.showToast === "function" && tryCall(toastCtx.showToast)) return;
      if (typeof toastCtx.addToast === "function" && tryCall(toastCtx.addToast)) return;
      if (typeof toastCtx.notify === "function" && tryCall(toastCtx.notify)) return;
      if (typeof toastCtx.push === "function" && tryCall(toastCtx.push)) return;
      return severity === "error" ? console.error(message) : console.log(message);
    },
    [toastCtx]
  );

  // ✅ Shared helper: choose correct auth + headers
  const getAuthContext = useCallback(async () => {
    const isActingAs = !!actAs?.isActingAs;

    const currentUser = isActingAs ? auth.currentUser : authPortal.currentUser;
    if (!currentUser) throw new Error(isActingAs ? "No staff user" : "No portal user");

    const idToken = await currentUser.getIdToken(true);
    const actAsHeaders = isActingAs ? actAs.getActAsHeaders() : {};
    const headers = { Authorization: `Bearer ${idToken}`, ...actAsHeaders };

    return { isActingAs, currentUser, idToken, headers };
  }, [actAs]);

  // ---- Load profile ----
  useEffect(() => {
    const isExpectingActAs = params.has("actAsClient") || params.has("as");
    if (isExpectingActAs && !actAs.isActingAs) return;

    (async () => {
      setLoading(true);
      try {
        const { headers } = await getAuthContext();

        const { data } = await axios.post(GET_CLIENT_PROFILE_URL, {}, { headers });
        const p = data?.profile || {};
        setProfile({
          fullName: p.fullName || "",
          email: p.email || "",
          phone: p.phone || "",
          altEmail: p.altEmail || "",
          photoURL: p.photoURL || "",
          address: {
            line1: p.address?.line1 || "",
            line2: p.address?.line2 || "",
            city: p.address?.city || "",
            state: p.address?.state || "",
            postalCode: p.address?.postalCode || "",
            country: p.address?.country || "",
          },
          dateOfBirth: p.dateOfBirth || "",
        });
      } catch (e) {
        console.error("Load profile failed:", e);
        toast("Failed to load your profile. Please refresh.", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [toast, actAs, params, getAuthContext]);

  // ---- Field handlers ----
  const handleChange = (field) => (e) =>
    setProfile((prev) => ({ ...prev, [field]: e.target.value }));

  const handleAddress = (field) => (e) =>
    setProfile((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: e.target.value },
    }));

  const handleSave = async () => {
    if (isReadOnly) return;
    setSaving(true);
    try {
      const { headers } = await getAuthContext();
      await axios.post(UPDATE_CLIENT_PROFILE_URL, profile, { headers });
      toast("Your information has been updated.", "success");
    } catch (e) {
      console.error("Save profile failed:", e);
      toast("Failed to save. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  // ---- Photo upload ----
  const onPickFile = () => {
    if (isReadOnly) return;
    fileRef.current?.click();
  };

  const onFileSelected = async (e) => {
    if (isReadOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_MB * 1024 * 1024) {
      toast(`File is too large (max ${MAX_MB}MB).`, "warning");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast("Unsupported image type. Please upload a JPG/PNG/GIF/WebP.", "warning");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      const { headers } = await getAuthContext();

      const fd = new FormData();
      fd.append("file", file, file.name);

      const { data } = await axios.post(UPLOAD_CLIENT_PHOTO_URL, fd, {
        headers,
        timeout: 30000,
      });

      const url = data?.photoURL || data?.url;
      if (url) {
        const busted = `${url}${url.includes("?") ? "&" : "?"}v=${Date.now()}`;
        setProfile((p) => ({ ...p, photoURL: busted }));
        toast("Photo updated.", "success");
      } else {
        toast("Upload succeeded but no URL returned.", "warning");
      }
    } catch (err) {
      console.error("Photo upload failed:", err);
      const apiMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to upload photo. Please try again.";
      toast(apiMsg, "error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // --------- Skeleton while loading ----------
  if (loading) {
    return (
      <Box>
        <Card>
          <CardHeader title={<Skeleton variant="text" width={180} height={28} />} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <Skeleton variant="circular" width={88} height={88} />
                  <Skeleton variant="rounded" width={120} height={32} />
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="50%" height={32} />
                <Skeleton variant="text" width="50%" height={32} />
                <Skeleton variant="text" width="40%" height={32} />

                <Divider sx={{ my: 2 }} />

                <Skeleton variant="text" width={120} height={22} />
                <Skeleton variant="text" width="70%" height={32} />
                <Skeleton variant="text" width="60%" height={32} />

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Skeleton variant="text" width="90%" height={32} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Skeleton variant="text" width="90%" height={32} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Skeleton variant="text" width="90%" height={32} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Skeleton variant="text" width="90%" height={32} />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Skeleton variant="text" width="50%" height={32} />
                <Box sx={{ mt: 2 }}>
                  <Skeleton variant="rounded" width={100} height={36} />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ---------------------- Loaded UI ----------------------
  return (
    <Box>
      {actAs.isActingAs && (
        <Box
          sx={{
            mb: 2,
            p: 1.25,
            borderRadius: 1,
            bgcolor: "#FFFBEB",
            border: "1px solid #FCD34D",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#92400E" }}>
            Staff preview: acting as{" "}
            <span style={{ textDecoration: "underline" }}>
              {actAs.actAsName || actAs.clientId}
            </span>
            . This page is read-only and actions are recorded.
          </Typography>
        </Box>
      )}

      <Card>
        <CardHeader title="My Information" />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Avatar
                  src={profile.photoURL || undefined}
                  sx={{ width: 88, height: 88 }}
                  alt={profile.fullName || "Profile photo"}
                />
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={onFileSelected}
                  disabled={isReadOnly}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onPickFile}
                  disabled={uploading || isReadOnly}
                >
                  {uploading ? "Uploading…" : "Upload Photo"}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={9}>
              <TextInputComponent
                label="Full Name"
                value={profile.fullName}
                onChange={handleChange("fullName")}
                readOnly={isReadOnly}
              />
              <TextInputComponent label="Email" value={profile.email} readOnly />
              <TextInputComponent
                label="Alternate Email"
                value={profile.altEmail}
                onChange={handleChange("altEmail")}
                readOnly={isReadOnly}
              />
              <TextInputComponent
                label="Phone Number"
                value={profile.phone}
                onChange={handleChange("phone")}
                readOnly={isReadOnly}
              />

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 1, fontWeight: 600, fontSize: 14 }}>Address</Box>
              <TextInputComponent
                label="Address Line 1"
                value={profile.address.line1}
                onChange={handleAddress("line1")}
                readOnly={isReadOnly}
              />
              <TextInputComponent
                label="Address Line 2"
                value={profile.address.line2}
                onChange={handleAddress("line2")}
                readOnly={isReadOnly}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextInputComponent
                    label="City"
                    value={profile.address.city}
                    onChange={handleAddress("city")}
                    readOnly={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextInputComponent
                    label="State / Province"
                    value={profile.address.state}
                    onChange={handleAddress("state")}
                    readOnly={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextInputComponent
                    label="Postal Code"
                    value={profile.address.postalCode}
                    onChange={handleAddress("postalCode")}
                    readOnly={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextInputComponent
                    label="Country"
                    value={profile.address.country}
                    onChange={handleAddress("country")}
                    readOnly={isReadOnly}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <DatePicker
                label="Date of Birth"
                value={profile.dateOfBirth ? dayjs(profile.dateOfBirth) : null}
                onChange={(newVal) =>
                  setProfile((p) => ({ ...p, dateOfBirth: fmtYmd(newVal) }))
                }
                disableMaskedInput
                disabled={isReadOnly}
                renderInput={(params) => (
                  <TextInputComponent
                    {...params}
                    fullWidth
                    size="small"
                    onKeyDown={(e) => e.preventDefault()}
                    onPaste={(e) => e.preventDefault()}
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || uploading || isReadOnly}
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}